/**
 * CommandPalette — the ⌘K / topbar-search command palette for the (app) layout.
 *
 * Opens when `commandPaletteOpen` flips in the UI store (the topbar Search button
 * toggles it) OR on ⌘K / Ctrl+K. Lists the same permission-gated pages as the
 * sidebar (via `filterVisibleNavItems`); selecting one navigates and closes.
 *
 * Mounted once in AppLayout so it's available on every internal page.
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useUIStore } from '@/stores/ui-store';
import { useAuthStore } from '@/stores/auth-store';
import { filterVisibleNavItems } from '@/components/layout/AppSidebar';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';

export function CommandPalette() {
  const commandPaletteOpen = useUIStore((s) => s.commandPaletteOpen);
  const setCommandPaletteOpen = useUIStore((s) => s.setCommandPaletteOpen);
  const toggleCommandPalette = useUIStore((s) => s.toggleCommandPalette);
  const navigate = useNavigate();
  const permissions = useAuthStore((s) => s.user?.permissions ?? []);

  const items = filterVisibleNavItems(permissions);

  // Global ⌘K / Ctrl+K to toggle the palette (matches the topbar hint).
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        toggleCommandPalette();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [toggleCommandPalette]);

  const go = (to: string) => {
    setCommandPaletteOpen(false);
    navigate(to);
  };

  return (
    <CommandDialog open={commandPaletteOpen} onOpenChange={setCommandPaletteOpen}>
      <CommandInput placeholder="Search pages…" aria-label="Search pages" />
      <CommandList>
        <CommandEmpty>No matching page.</CommandEmpty>
        <CommandGroup heading="Go to">
          {items.map((item) => {
            const Icon = item.icon;
            return (
              <CommandItem
                key={item.to}
                value={`${item.label} ${item.to}`}
                onSelect={() => go(item.to)}
              >
                <Icon className="mr-2 size-4" aria-hidden="true" />
                <span>{item.label}</span>
              </CommandItem>
            );
          })}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
